<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-header>
        <h4>{{ $t('accounting_transactions_create_cash_out') }}</h4>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="form">
          <b-form @submit.prevent="onSubmit">
            <b-row>
              <receiver-chart-fields
                :value="form.fund_gl_account_id"
                :label="$t('accounting_paid_from')"
                input-id="fund_gl_account_id"
                :blocked-id="form.receiver_gl_account_id"
                :excluded-ids="excludedChartIds"
                :server-error="fundError"
                @input="onFundChart"
              />
              <receiver-chart-fields
                :value="form.receiver_gl_account_id"
                :label="$t('accounting_received_by')"
                input-id="receiver_gl_account_id"
                :blocked-id="form.fund_gl_account_id"
                :excluded-ids="excludedChartIds"
                :server-error="receiverError"
                @input="onReceiverChart"
              />
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Amount"
                  rules="required|min_value:0.01"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_amount')"
                    label-for="amount"
                  >
                    <b-form-input
                      id="amount"
                      v-model.number="form.amount"
                      type="number"
                      step="0.01"
                      :placeholder="$t('accounting_transactions_amount')"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12">
                <transaction-money-fields
                  direction="expense"
                  :amount="form.amount"
                  :fee.sync="form.fee"
                  :file.sync="invoiceFile"
                  :balance="selectedBalance"
                />
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Transaction Date"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_transaction_date')"
                    label-for="transaction_date"
                  >
                    <b-form-input
                      id="transaction_date"
                      v-model="form.transaction_date"
                      type="date"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('accounting_transactions_project')"
                  label-for="project_id"
                >
                  <payroll-project-select
                    id="project_id"
                    :value="form.project_id"
                    :placeholder="$t('accounting_transactions_project')"
                    @input="form.project_id = $event"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Transaction Reason"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_transaction_reason')"
                    label-for="transaction_reason"
                  >
                    <v-select
                      id="transaction_reason"
                      v-model="form.transaction_reason"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      :options="transactionReasonOptions"
                      :reduce="val => val.value"
                      :get-option-label="option => $i18n.locale === 'ar' ? option.label_ar : option.label"
                      :placeholder="$t('accounting_transactions_transaction_reason')"
                      :loading="loadingReasons"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12">
                <b-form-group
                  :label="$t('accounting_transactions_description')"
                  label-for="description"
                >
                  <b-form-textarea
                    id="description"
                    v-model="form.description"
                    rows="3"
                    :placeholder="$t('accounting_transactions_description')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mt-2"
              >
                <b-button
                  type="submit"
                  variant="primary"
                  class="mr-1"
                  :disabled="submitting"
                >
                  {{ submitting ? $t('accounting_transactions_saving') : $t('accounting_transactions_save') }}
                </b-button>
                <b-button
                  variant="secondary"
                  @click="$router.go(-1)"
                >
                  {{ $t('accounting_transactions_cancel_btn') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min_value } from '@validations'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountsAPI from '@/api/accounting/accounts'
import * as walletsAPI from '@/api/accounting/wallets'
import * as transactionReasonsAPI from '@/api/accounting/transactionReasons'
import { withInvoiceFile } from '@/utils/transactionMoney'
import {
  drawerChartIds, fetchActivePayouts, fieldError, payoutOptionLabel, voucherErrorMessage, walletOptionLabel,
} from '@/utils/voucherSource'
import TransactionMoneyFields from './TransactionMoneyFields.vue'
import ReceiverChartFields from './ReceiverChartFields.vue'
import PayrollProjectSelect from '../ledger/components/PayrollProjectSelect.vue'

const CHART_ERROR_KEYS = {
  'Chart account is inactive or does not accept posting.': 'accounting_fund_gl_inactive',
  'Receiving chart account is inactive or does not accept posting.': 'accounting_receiver_inactive',
}

function localToday() {
  const date = new Date()
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    ToastificationContent,
    TransactionMoneyFields,
    ReceiverChartFields,
    PayrollProjectSelect,
  },
  data() {
    return {
      submitting: false,
      loadingAccounts: false,
      loadingWallets: false,
      loadingPayouts: false,
      loadingReasons: false,
      accounts: [],
      wallets: [],
      transactionReasonOptions: [],
      invoiceFile: null,
      required,
      min_value,
      form: {
        transaction_reason: null,
        amount: null,
        fee: 0,
        description: null,
        payment_source: null,
        source_id: null,
        fund_gl_account_id: null,
        receiver_gl_account_id: null,
        project_id: null,
        receipt_path: null,
        transaction_date: localToday(),
      },
      payoutAccounts: [],
      sourceError: '',
      fundError: '',
      receiverError: '',
    }
  },
  computed: {
    paymentSourceLabel() {
      if (this.form.payment_source === 'wallet') return this.$t('accounting_transactions_custody_wallet')
      if (this.form.payment_source === 'employee_payment_account') return this.$t('accounting_transactions_employee_payment_account')
      if (this.form.payment_source === 'account') return this.$t('accounting_transactions_company_account')
      return this.$t('accounting_transactions_source')
    },
    paymentSourceOptions() {
      return [
        { value: 'account', label: this.$t('accounting_transactions_company_account') },
        { value: 'wallet', label: this.$t('accounting_transactions_custody_wallet') },
        { value: 'employee_payment_account', label: this.$t('accounting_transactions_employee_payment_account') },
      ]
    },
    sourceOptions() {
      if (this.form.payment_source === 'wallet') return this.wallets
      if (this.form.payment_source === 'account') return this.accounts
      if (this.form.payment_source === 'employee_payment_account') return this.payoutAccounts
      return []
    },
    selectedBalance() {
      if (this.form.payment_source === 'employee_payment_account') return null
      const source = (this.sourceOptions || []).find(item => item.id === this.form.source_id)
      return source ? source.current_balance : null
    },
    excludedChartIds() {
      return drawerChartIds([...(this.accounts || []), ...(this.wallets || []), ...(this.payoutAccounts || [])])
    },
    sourceLoading() {
      if (this.form.payment_source === 'wallet') return this.loadingWallets
      if (this.form.payment_source === 'account') return this.loadingAccounts
      if (this.form.payment_source === 'employee_payment_account') return this.loadingPayouts
      return false
    },
  },
  async mounted() {
    await Promise.all([
      this.loadAccounts(),
      this.loadWallets(),
      this.loadPayouts(),
      this.loadTransactionReasons(),
    ])
  },
  methods: {
    async onSubmit() {
      this.sourceError = ''
      this.fundError = ''
      this.receiverError = ''
      const isValid = await this.$refs.form.validate()
      if (!isValid || !this.form.fund_gl_account_id || !this.form.receiver_gl_account_id) return

      this.submitting = true
      try {
        await this.$store.dispatch('accounting/expenseTransactions/create', withInvoiceFile({
          transaction_reason: this.form.transaction_reason,
          amount: this.form.amount,
          fee: this.form.fee,
          description: this.form.description || null,
          fund_gl_account_id: this.form.fund_gl_account_id,
          receiver_gl_account_id: this.form.receiver_gl_account_id,
          project_id: this.form.project_id || null,
          receipt_path: this.form.receipt_path || null,
          transaction_date: this.form.transaction_date,
        }, this.invoiceFile))
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.created_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$router.push({ name: 'accounting-transactions-list' })
      } catch (error) {
        this.sourceError = fieldError(error, 'source_id')
        const fundRaw = fieldError(error, 'fund_gl_account_id')
        const receiverRaw = fieldError(error, 'receiver_gl_account_id')
        this.fundError = CHART_ERROR_KEYS[fundRaw] ? this.$t(CHART_ERROR_KEYS[fundRaw]) : fundRaw
        this.receiverError = CHART_ERROR_KEYS[receiverRaw] ? this.$t(CHART_ERROR_KEYS[receiverRaw]) : receiverRaw
        const errorMessage = voucherErrorMessage(error, this.$t('accounting_transactions_messages.error'))
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },
    onPaymentSourceChange() {
      this.form.source_id = null
      this.sourceError = ''
    },
    onFundChart(id) {
      this.form.fund_gl_account_id = id
      this.fundError = ''
    },
    onReceiverChart(id) {
      this.form.receiver_gl_account_id = id
      this.receiverError = ''
    },
    getSourceOptionLabel(option) {
      if (!option) return ''
      if (this.form.payment_source === 'wallet') return walletOptionLabel(option)
      if (this.form.payment_source === 'employee_payment_account') return payoutOptionLabel(option)
      return `${option.code} - ${option.name}`
    },
    async loadPayouts() {
      this.loadingPayouts = true
      try {
        this.payoutAccounts = await fetchActivePayouts()
      } catch (error) {
        console.error('Error loading payout accounts:', error)
        this.payoutAccounts = []
      } finally {
        this.loadingPayouts = false
      }
    },
    async loadAccounts() {
      this.loadingAccounts = true
      try {
        const response = await accountsAPI.getAccounts({ is_active: true, per_page: 1000 })
        this.accounts = response.data || []
      } catch (error) {
        console.error('Error loading accounts:', error)
      } finally {
        this.loadingAccounts = false
      }
    },
    async loadWallets() {
      this.loadingWallets = true
      try {
        const response = await walletsAPI.getWallets({ status: 'active', per_page: 1000 })
        this.wallets = response.data || []
      } catch (error) {
        console.error('Error loading wallets:', error)
      } finally {
        this.loadingWallets = false
      }
    },
    async loadTransactionReasons() {
      this.loadingReasons = true
      try {
        const response = this.$i18n.locale === 'ar'
          ? await transactionReasonsAPI.getExpenseTransactionReasonOptionsAr()
          : await transactionReasonsAPI.getExpenseTransactionReasonOptions()

        // The request interceptor returns response.data, so response is already unwrapped
        // Handle different response structures
        let reasons = []
        if (Array.isArray(response)) {
          // Response is directly an array
          reasons = response
        } else if (response && response.data && Array.isArray(response.data)) {
          // Response has { data: [...] }
          reasons = response.data
        } else if (response && response.success && Array.isArray(response.data)) {
          // Response has { success: true, data: [...] }
          reasons = response.data
        } else if (response && response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Response has { data: { "key": "value", ... } } - object format from /options or /options-ar endpoint
          // Transform object to array format: [{ value: "key", label: "...", label_ar: "..." }]
          const dataObj = response.data
          const isArabic = this.$i18n.locale === 'ar'

          // Get both English and Arabic labels for complete data
          let englishLabels = {}
          let arabicLabels = dataObj

          try {
            if (isArabic) {
              // Current response has Arabic, fetch English
              const enResponse = await transactionReasonsAPI.getExpenseTransactionReasonOptions()
              if (enResponse && enResponse.data && typeof enResponse.data === 'object' && !Array.isArray(enResponse.data)) {
                englishLabels = enResponse.data
              }
            } else {
              // Current response has English, fetch Arabic
              const arResponse = await transactionReasonsAPI.getExpenseTransactionReasonOptionsAr()
              if (arResponse && arResponse.data && typeof arResponse.data === 'object' && !Array.isArray(arResponse.data)) {
                arabicLabels = arResponse.data
              }
              englishLabels = dataObj
            }
          } catch (e) {
            // If fetching the other language fails, use current response data
            if (isArabic) {
              englishLabels = {}
            } else {
              arabicLabels = {}
            }
          }

          // Merge into array format
          const allKeys = new Set([...Object.keys(englishLabels), ...Object.keys(arabicLabels)])
          reasons = Array.from(allKeys).map(key => ({
            value: key,
            label: englishLabels[key] || key,
            label_ar: arabicLabels[key] || key,
          }))
        } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          // Nested response structure
          reasons = response.data.data
        } else {
          console.warn('Unexpected response structure:', response)
        }

        this.transactionReasonOptions = reasons || []
      } catch (error) {
        console.error('Error loading transaction reasons:', error)
        if (error.response) {
          console.error('Error response:', error.response.data)
        }
        this.transactionReasonOptions = []
      } finally {
        this.loadingReasons = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
